import { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Fade,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TableSortLabel,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Info from './Info';
import LanguageSelector from './LanguageSelector';
import { useAdminLanguage } from './AdminI18n';
import useTableSort from './useTableSort';

function DropOffAdmin() {
  const { t } = useAdminLanguage();
  const [dropoffs, setDropoffs] = useState([]);
  const [formData, setFormData] = useState({ prefixe: '', lieuDepot: '' });
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [language, setLanguage] = useState('fr');



  useEffect(() => {
    fetchDropoffs();
  }, [language]);

  const fetchDropoffs = async () => {
    try {
      const response = await fetch(`/api/dropoffs?lang=${language}`);
      const data = await response.json();
      setDropoffs(data);
      setLoading(false);
    } catch (err) {
      setError('Erreur lors du chargement des lieux de dépôt');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      if (!formData.prefixe.trim() || !formData.lieuDepot.trim()) {
        setError('Tous les champs sont obligatoires');
        return;
      }

      setLoading(true);
      setError('');
      setSuccess('');

      if (isEditing) {
        await fetch(`/api/dropoffs/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...formData, lang: language }),
        });
        setSuccess('Lieu de dépôt mis à jour avec succès');
      } else {
        await fetch('/api/dropoffs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...formData, lang: language }),
        });
        setSuccess('Lieu de dépôt ajouté avec succès');
      }

      setFormData({ prefixe: '', lieuDepot: '' });
      setIsEditing(false);
      setEditingId(null);
      fetchDropoffs();
    } catch (err) {
      setError('Erreur lors de l\'enregistrement du lieu de dépôt');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (dropoff) => {
    setIsEditing(true);
    setEditingId(dropoff.id);
    setFormData({ prefixe: dropoff.prefixe, lieuDepot: dropoff.lieuDepot });
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await fetch(`/api/dropoffs/${id}?lang=${language}`, {
        method: 'DELETE',
      });
      setSuccess('Lieu de dépôt supprimé avec succès');
      fetchDropoffs();
    } catch (err) {
      setError('Erreur lors de la suppression du lieu de dépôt');
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(() => ([
    { id: 'prefixe', label: t('Préfixe'), sortable: true },
    { id: 'lieuDepot', label: t('Lieu de dépôt'), sortable: true },
  ]), [t]);

  const { order, orderBy, sortedRows, handleRequestSort } = useTableSort(dropoffs, {
    defaultOrderBy: 'prefixe',
  });

  const renderCellContent = (row, columnId) => row[columnId];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Fade in timeout={500}>
        <Box>
          <Typography
            variant="h4"
            gutterBottom
            align="center"
            color="primary"
            sx={{ fontWeight: 'bold', mb: 4 }}
          >
            {t('Gestion des lieux de dépôt')}
          </Typography>

          <Info type="dropoff" />

          <LanguageSelector 
            language={language} 
            onLanguageChange={setLanguage} 
          />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t(error)}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t(success)}
            </Alert>
          )}

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              {t(isEditing ? 'Modifier le lieu de dépôt' : 'Ajouter un lieu de dépôt')}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="prefixe"
                  label={t('Préfixe (ex : entre les mains du, auprès de, chez, au)')}
                  autoComplete="off"
                  value={formData.prefixe}
                  onChange={handleInputChange}
                  variant="outlined"
                  required
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="lieuDepot"
                  label={t('Lieu de dépôt')}
                  autoComplete="off"
                  value={formData.lieuDepot}
                  onChange={handleInputChange}
                  variant="outlined"
                  required
                  disabled={loading}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit}
                  disabled={loading}
                  startIcon={loading && <CircularProgress size={20} />}
                >
                  {loading ? t('Enregistrement...') : t(isEditing ? 'Modifier' : 'Ajouter')}
                </Button>
                {isEditing && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      setIsEditing(false);
                      setEditingId(null);
                      setFormData({ prefixe: '', lieuDepot: '' });
                    }}
                    sx={{ ml: 2 }}
                  >
                    {t('Annuler')}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography variant="h6" gutterBottom>
              {t('Liste des lieux de dépôt')}
            </Typography>

            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.id}
                        sortDirection={orderBy === column.id ? order : false}
                      >
                        {column.sortable ? (
                          <TableSortLabel
                            active={orderBy === column.id}
                            direction={orderBy === column.id ? order : 'asc'}
                            onClick={() => handleRequestSort(column.id)}
                          >
                            {column.label}
                          </TableSortLabel>
                        ) : (
                          column.label
                        )}
                      </TableCell>
                    ))}
                    <TableCell>{t('Actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedRows.map((dropoff) => (
                    <TableRow key={dropoff.id}>
                      {columns.map((column) => (
                        <TableCell key={`${dropoff.id}-${column.id}`}>
                          {renderCellContent(dropoff, column.id)}
                        </TableCell>
                      ))}
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'nowrap' }}>
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(dropoff)}
                            color="primary"
                            disabled={loading}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(dropoff.id)}
                            color="error"
                            disabled={loading}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Fade>
    </Container>
  );
}

export default DropOffAdmin;
